import { FlowContext } from 'src/lib/flow';
import { composePage, FlowPage, newFlowPage } from 'src/lib/flow/FlowPage';
import { addPageLoader } from 'src/lib/flow/loaders';
import { isAgent } from 'src/lib/flow/pageHelpers';
import { getFlowFromState } from 'src/store/flow_listeners';
import { changeAnswers } from 'src/store/sellerInput';

import {
  bathroomCondition,
  exteriorCondition,
  kitchenCondition,
  livingRoomCondition,
} from './roomCondition';

const isEnabled = ({ answers, inputContext: { channel, sellerFlowName } }: FlowContext) => {
  const isInUntreatedRoomConditionExperiment = sellerFlowName === 'untreated_room_condition';
  return isAgent(answers, channel) && !isInUntreatedRoomConditionExperiment;
};

const makeQuestionOptional = (page: FlowPage) => (ctx: FlowContext) => {
  const questions = page.questions(ctx);
  return [{ ...questions[0], optional: true }];
};

const roomConditionPages = [
  composePage(kitchenCondition, {
    isEnabled,
    slug: () => 'kitchen-condition-agent',
    showSkipButton: () => true,
    questions: makeQuestionOptional(kitchenCondition),
  }),
  composePage(bathroomCondition, {
    isEnabled,
    slug: () => 'bathroom-condition-agent',
    showSkipButton: () => true,
    questions: makeQuestionOptional(bathroomCondition),
  }),
  composePage(livingRoomCondition, {
    isEnabled,
    slug: () => 'living-room-condition-agent',
    showSkipButton: () => true,
    questions: makeQuestionOptional(livingRoomCondition),
  }),
  composePage(exteriorCondition, {
    isEnabled,
    slug: () => 'exterior-condition-agent',
    showSkipButton: () => true,
    questions: makeQuestionOptional(exteriorCondition),
  }),
];

// If a user skips any of these pages we need to set the value to `standard`

// First create a "virtual" page so that we can hook into the flow page
// lifecycle and add a page loader. This page will never be enabled but loaders
// always run for pages even when the page is not enabled.
//
// This does work, but causes the exterior-condition-agent page to
// flash "standard" being selected before the page continues. This is why for
// the other pages we do not create a virtual page.
//
// We could update the page after exterior-condition-agent, but by having this
// virtual page we keep all the page dependencies clearly stated in this file
const saveExteriorDefaultPage = newFlowPage({
  title: '',
  slug: 'save-exterior-default',
  questions: [],
  isEnabled: () => false,
  autoAdvance: () => true,
});

// Next add page loaders that will check if the previous page has an answer
// and if not then dispatch an update to set the previous page's answer to
// standard
[
  ['bathroom-condition-agent', 'home.kitchen_seller_score'],
  ['living-room-condition-agent', 'home.bathroom_seller_score'],
  ['exterior-condition-agent', 'home.living_room_seller_score'],
  ['save-exterior-default', 'home.exterior_seller_score'],
].map(([slug, key]) => {
  addPageLoader(slug, 'save-default', async (listenerApi) => {
    const state = listenerApi.getState();
    const { context } = getFlowFromState(listenerApi.getState());
    if (!isEnabled(context)) return;
    if (!state.sellerInput.savedAnswers[key] && !state.sellerInput.changedAnswers[key]) {
      listenerApi.dispatch(changeAnswers([{ key, answer: 'standard' }]));
    }
  });
});

export default [...roomConditionPages, saveExteriorDefaultPage];
